import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

// Empty default → baseURL relativo '/api'. Nginx admin faz reverse proxy pro gateway
// em producao/VPS (evita CORS + localhost:3000 do browser externo).
const API_URL = import.meta.env.VITE_API_URL || '';
const USE_COOKIES = import.meta.env.VITE_ADMIN_AUTH_COOKIES === 'true';

/**
 * Axios instance para o Gateway REST.
 *
 * Dois modos, gated por VITE_ADMIN_AUTH_COOKIES:
 * - 'true'  → HttpOnly cookies (EPIC-004 F1/E-002). Sem Authorization header.
 *             Interceptor 401 chama POST /auth/admin/refresh (cookie-based) e reexecuta.
 * - outro   → fallback legado Bearer via localStorage + X-User-Id/X-Caller-Role.
 *
 * `withCredentials: true` em ambos os modos (legacy já enviava cookies cross-origin).
 */
export const api: AxiosInstance = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 15_000,
  withCredentials: true,
});

// ── CSRF helpers (cookie mode) ─────────────────────────────
// Backend emite cookie __Host-csrf no host da API (Set-Cookie em api.staging.*).
// Quando o admin roda em admin.staging.* e chama API cross-origin, o cookie e
// enviado nas requests (withCredentials) mas o JS do admin NAO consegue ler via
// document.cookie (cookies sao isolados por origin). Por isso, em vez de ler do
// cookie, guardamos o csrf_token retornado no body do endpoint /auth/admin/csrf
// em memoria JS (module-level) e injetamos no header X-CSRF-Token. O backend
// faz double-submit: header vs cookie em cada mutation.

let cachedCsrfToken: string | null = null;

function readCsrfCookie(): string | null {
  // Fallback: tenta document.cookie (same-origin OR cookies com Domain explicito).
  const raw = document.cookie;
  if (raw) {
    for (const part of raw.split(';')) {
      const [k, ...rest] = part.trim().split('=');
      if (k === '__Host-csrf' || k === 'csrf') {
        return decodeURIComponent(rest.join('='));
      }
    }
  }
  return null;
}

function isMutationMethod(method?: string): boolean {
  if (!method) return false;
  const m = method.toUpperCase();
  return m === 'POST' || m === 'PUT' || m === 'PATCH' || m === 'DELETE';
}

// ── CSRF prefetch single-flight ────────────────────────────
// Antes da primeira mutação, garante csrf_token em memória. GET /auth/admin/csrf
// retorna { csrf_token } + seta cookie no host da API. Sem cache compartilhado
// (axios direct fetch usando window.fetch raw — evita race com o proprio
// interceptor + simplifica lifecycle).
let csrfPrefetchPromise: Promise<string | null> | null = null;
async function ensureCsrfToken(): Promise<string | null> {
  if (cachedCsrfToken) return cachedCsrfToken;
  // Cross-origin (admin.staging.* -> api.staging.*): cookies __Host-csrf ficam no
  // host da API e nao sao legiveis por JS no admin. Cookies com mesmo nome em
  // admin.staging.* sao residuo de sessoes antigas (site legacy) e referem tokens
  // que nao existem na DB do admin-svc — usar isso resulta em CSRF_INVALID.
  // Same-origin (dev local via proxy) usa o fallback do cookie.
  if (!API_URL) {
    const fromCookie = readCsrfCookie();
    if (fromCookie) {
      cachedCsrfToken = fromCookie;
      return cachedCsrfToken;
    }
  }
  if (csrfPrefetchPromise) return csrfPrefetchPromise;
  // CRITICAL: usa fetch raw (NAO axios) para evitar que esta chamada entre no
  // mesmo interceptor + dispare loops/races em refresh-on-401. Mantem
  // withCredentials para enviar cookies da API host.
  const url = `${API_URL}/api/auth/admin/csrf`;
  csrfPrefetchPromise = (async () => {
    try {
      const resp = await fetch(url, { credentials: 'include' });
      if (!resp.ok) return null;
      const data = (await resp.json()) as { success?: boolean; csrf_token?: string };
      const token = data?.csrf_token ?? null;
      if (token) cachedCsrfToken = token;
      return token;
    } catch {
      return null;
    } finally {
      csrfPrefetchPromise = null;
    }
  })();
  return csrfPrefetchPromise;
}

/** Limpa o token CSRF cacheado — chame em logout ou em 403 CSRF_INVALID. */
export function clearCachedCsrfToken(): void {
  cachedCsrfToken = null;
}

// ── Request interceptor ────────────────────────────────────
// Legacy mode injeta Bearer + headers de identidade.
// Cookie mode injeta X-CSRF-Token em mutacoes (double-submit cookie — EPIC-004 F4/E-008).
api.interceptors.request.use(async (config) => {
  if (!USE_COOKIES) {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers = config.headers ?? {};
      (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
    const userId = localStorage.getItem('admin_user_id');
    if (userId) {
      config.headers = config.headers ?? {};
      (config.headers as Record<string, string>)['X-User-Id'] = userId;
      (config.headers as Record<string, string>)['X-Caller-Role'] = 'admin';
    }
    return config;
  }

  if (isMutationMethod(config.method)) {
    const csrf = await ensureCsrfToken();
    if (csrf) {
      config.headers = config.headers ?? {};
      (config.headers as Record<string, string>)['X-CSRF-Token'] = csrf;
    }
  }
  return config;
});

// ── Response interceptor ───────────────────────────────────
// Legacy: 401 → limpa storage e redireciona para /login.
// Cookie mode: 401 → tenta refresh single-flight; enfileira requests concorrentes;
//   se refresh falhar (ou 401 novamente), limpa sessão e redireciona.
type QueuedResolver = {
  resolve: (value: unknown) => void;
  reject: (err: unknown) => void;
};

let isRefreshing = false;
let refreshQueue: QueuedResolver[] = [];

function flushQueue(error: unknown) {
  for (const { resolve, reject } of refreshQueue) {
    if (error) reject(error);
    else resolve(undefined);
  }
  refreshQueue = [];
}

function redirectToLogin() {
  // Limpa zustand+localStorage da admin session antes de redirecionar —
  // evita loop /dashboard <-> /login (BUG #5). Cookie ja foi invalidado
  // pelo backend (revoke ou expire); aqui sincronizamos o estado do cliente.
  try {
    const raw = localStorage.getItem('ouropix-admin-auth');
    if (raw) {
      localStorage.setItem(
        'ouropix-admin-auth',
        JSON.stringify({ state: { session: null }, version: 0 }),
      );
    }
  } catch {
    // ignore
  }
  cachedCsrfToken = null;
  if (!window.location.pathname.startsWith('/login')) {
    window.location.href = '/login';
  }
}

function clearLegacyStorage() {
  localStorage.removeItem('admin_token');
  localStorage.removeItem('admin_user_id');
}

/** Flag no request config indicando que ja tentamos refresh — evita loop. */
type RetryConfig = InternalAxiosRequestConfig & { _retried?: boolean };

// Atualiza cache CSRF quando o backend rotaciona o token e expoe via X-CSRF-Token header.
// CsrfGuard rotaciona apos cada validate-and-consume (token e one-shot).
function captureRotatedCsrf(r: { headers?: Record<string, string> | unknown }): void {
  const hdrs = r?.headers as Record<string, string> | undefined;
  if (!hdrs) return;
  const next =
    hdrs['x-csrf-token'] ??
    (hdrs as Record<string, string>)['X-CSRF-Token'] ??
    null;
  if (next) cachedCsrfToken = next;
}

api.interceptors.response.use(
  (r) => {
    captureRotatedCsrf(r);
    return r;
  },
  async (error) => {
    if (error?.response) captureRotatedCsrf(error.response);
    // 403 CSRF_INVALID → invalida cache + permite uma re-tentativa
    if (error?.response?.status === 403 && error.response.data?.code === 'CSRF_INVALID') {
      cachedCsrfToken = null;
      const original = error.config as InternalAxiosRequestConfig & { _csrfRetried?: boolean };
      if (original && !original._csrfRetried) {
        original._csrfRetried = true;
        return api(original);
      }
    }
    const status = error?.response?.status;
    const original = error?.config as RetryConfig | undefined;

    if (status !== 401 || !original) {
      return Promise.reject(error);
    }

    if (!USE_COOKIES) {
      clearLegacyStorage();
      redirectToLogin();
      return Promise.reject(error);
    }

    // Cookie mode
    // Se ja tentamos refresh nesta request, desistimos.
    if (original._retried) {
      redirectToLogin();
      return Promise.reject(error);
    }

    // Nao tentar refresh no proprio endpoint de refresh (evita recursao infinita).
    const url = original.url ?? '';
    if (url.includes('/auth/admin/refresh') || url.includes('/auth/admin/login')) {
      redirectToLogin();
      return Promise.reject(error);
    }

    original._retried = true;

    if (isRefreshing) {
      // Enfileira ate o refresh em curso terminar.
      return new Promise((resolve, reject) => {
        refreshQueue.push({
          resolve: () => {
            resolve(api(original as AxiosRequestConfig));
          },
          reject,
        });
      });
    }

    isRefreshing = true;
    try {
      // Refresh via cookie HttpOnly — nenhum body necessario.
      await axios.post(
        `${API_URL}/api/auth/admin/refresh`,
        {},
        { withCredentials: true },
      );
      flushQueue(null);
      isRefreshing = false;
      return api(original as AxiosRequestConfig);
    } catch (refreshErr) {
      flushQueue(refreshErr);
      isRefreshing = false;
      redirectToLogin();
      return Promise.reject(refreshErr);
    }
  },
);
